import React from "react";

import { ContextMenu } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useQueryClient } from "@tanstack/react-query";

import TasksService from "@/services/TasksService";

import { useCanWriteTasks } from "@/hooks/useActiveRole";
import { useContextMenus } from "@/hooks/useContextMenus";
import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useHomeNavigation } from "@/hooks/useHomeNavigation";
import { useSelection } from "@/hooks/useSelection";

import { DeleteTasksConfirmModal } from "@/components/molecules";

import { isTaskDependentQuery } from "@/constants/CQueryKeys";

import { TTask } from "@/types";

import { promiseToast } from "@/modules/index";

export const TasksListCtxMenu = () => {
	const { selectItem } = useSelection<TTask>();
	const { taskId: activeTaskId } = useHomeLocation();
	const { toTask, toNewTask, closeTask } = useHomeNavigation();
	// Right-click opens this menu whatever the role: a VIEWER only gets
	// "Select" (the API refuses the rest), same rule as TasksList.
	const readOnly = !useCanWriteTasks();
	const queryClient = useQueryClient();
	const { tasksList: menu } = useContextMenus();
	const [showConfirmModal, setShowConfirmModal] = React.useState<TTask>();

	const handleRemove = (task?: TTask, confirmed = false) => {
		if (!confirmed) {
			setShowConfirmModal(task);
			return;
		}
		const prom = TasksService.deleteTask(String(task!.id)).then(() => {
			if (activeTaskId === task?.id) closeTask();
			queryClient.invalidateQueries({
				predicate: (query) => isTaskDependentQuery(query.queryKey),
			});
		});

		promiseToast(prom);
		return prom;
	};

	const handleEdit = (task: TTask) => toTask(task.id, { edit: true });

	const handleSelect = (task: TTask) => {
		selectItem(task);
	};

	// The new-task form copies the source itself (name, notes, project).
	const handleDuplicate = (task: TTask) => toNewTask({ fromTaskId: task.id });

	return (
		<div>
			<ContextMenu
				menu={menu}
				items={(task) => [
					{
						label: t("actions.edit"),
						picto: "edit",
						hidden: readOnly,
						onClick: () => handleEdit(task),
					},
					{
						label: t("actions.select"),
						picto: "addCircle",
						onClick: () => handleSelect(task),
					},
					{
						label: t("actions.duplicate"),
						picto: "copy",
						hidden: readOnly,
						onClick: () => handleDuplicate(task),
					},
					{
						label: t("actions.delete"),
						picto: "trash",
						color: "danger",
						hidden: readOnly,
						onClick: () => handleRemove(task),
					},
				]}
			/>

			<DeleteTasksConfirmModal
				items={showConfirmModal ? [showConfirmModal] : []}
				onConfirm={() => {
					handleRemove(showConfirmModal, true);
					setShowConfirmModal(undefined);
				}}
				open={!!showConfirmModal}
				onClose={() => {
					setShowConfirmModal(undefined);
				}}
			/>
		</div>
	);
};
